import {
  ApplicationConfig,
  EnvironmentProviders,
  importProvidersFrom,
  provideZoneChangeDetection,
  APP_INITIALIZER,
} from '@angular/core';
import { DATE_PIPE_DEFAULT_OPTIONS } from '@angular/common';
import { provideRouter, withEnabledBlockingInitialNavigation } from '@angular/router';
import { routes } from './app.routes';
import { BrowserModule } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import {
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalBroadcastService,
  MsalGuard,
  MsalInterceptor,
  MsalService,
} from '@azure/msal-angular';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NbEvaIconsModule } from '@nebular/eva-icons';
import {
  NbAutocompleteModule,
  NbTooltipModule,
  NbToggleModule,
  NbProgressBarModule,
  NbListModule,
  NbCheckboxModule,
  NbSelectModule,
  NbSpinnerModule,
  NbInputModule,
  NbThemeModule,
  NbSidebarModule,
  NbMenuModule,
  NbDatepickerModule,
  NbTimepickerModule,
  NbDialogModule,
  NbWindowModule,
  NbToastrModule,
  NbChatModule,
  NbLayoutModule,
  NbCardModule,
  NbIconModule,
  NbButtonModule,
  NbDateService,
} from '@nebular/theme';
import {
  MSALGuardConfigFactory,
  MSALInstanceFactory,
  MSALInterceptorConfigFactory,
  initializeClientId,
  initializeMsalRedirectHandling,
} from './core/auth/auth.config';
import { MenuStore } from './core/store/menu.store';
import { SystemImageStore } from './core/store/system-image.store';
import { DATE_FORMAT } from './shared/constants/date-format.constant';
import { AppNativeDateService } from './shared/services/app-native-date.service';
import { provideAngularQuery, QueryClient } from '@tanstack/angular-query-experimental';

const provideNebular = (): EnvironmentProviders[] => [
  importProvidersFrom(
    NbThemeModule.forRoot({ name: 'default' }),
    NbSidebarModule.forRoot(),
    NbMenuModule.forRoot(),
    NbDatepickerModule.forRoot(),
    NbTimepickerModule.forRoot(),
    NbDialogModule.forChild(),
    NbWindowModule.forRoot(),
    NbToastrModule.forRoot(),
    NbChatModule.forRoot({
      messageGoogleMapKey: 'AIzaSyA_wNuCzia92MAmdLRzmqitRGvCF7wCZPY',
    }),
    NbAutocompleteModule,
    NbTooltipModule,
    NbToggleModule,
    NbProgressBarModule,
    NbListModule,
    NbCheckboxModule,
    NbSelectModule,
    NbSpinnerModule,
    NbInputModule,
    NbLayoutModule,
    NbCardModule,
    NbEvaIconsModule,
    NbIconModule,
    NbButtonModule
  ),
];

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes, withEnabledBlockingInitialNavigation()),
    importProvidersFrom(BrowserModule, FormsModule, ReactiveFormsModule),
    ...provideNebular(),
    // Must come after provideNebular() to replace the NbNativeDateService it registers
    { provide: NbDateService, useClass: AppNativeDateService },
    { provide: DATE_PIPE_DEFAULT_OPTIONS, useValue: { dateFormat: DATE_FORMAT } },
    provideAnimations(),
    provideHttpClient(withInterceptorsFromDi()),
    provideAngularQuery(
      new QueryClient({
        defaultOptions: {
          queries: {
            experimental_prefetchInRender: true,
          },
        },
      })
    ),
    {
      provide: APP_INITIALIZER,
      useFactory: initializeClientId,
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeMsalRedirectHandling,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true,
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    MenuStore,
    SystemImageStore,
  ],
};
